import { Divider, Flex, Grid, Loader, NavLink, ScrollArea, Text, Title } from '@mantine/core';
import { renderCommentType } from '../../../scripts/build-docs/render';
import { useConstants } from '../../hooks/use-constants';
import type { ConstantValue, DocCategory, DocComment } from '../../types/constants';
import './Constants.css';

function renderComment(comment: DocComment) {
  return (
    <tr>
      <th>{renderCommentType(comment.type)}</th>
      <td>
        {comment.type === 'link' ? (
          <a href={comment.text} target="_blank" rel="noopener noreferrer">
            link
          </a>
        ) : (
          comment.text
        )}
      </td>
    </tr>
  );
}

function quoteString(value: string): string {
  if (value?.includes(' ')) {
    return `"${value}"`;
  }
  return value;
}

type SectionValuesTableProps = {
  values: ConstantValue[];
};
function SectionValuesTable({ values }: SectionValuesTableProps) {
  const hasComments = values.some((value) => value.comments?.length > 0 && value.path.length > 2);
  const hasUnit = values.some((value) => value.unit);

  const headers = ['Path', ...(hasComments ? ['Comments'] : []), 'Value', ...(hasUnit ? ['Unit'] : [])];
  const cellValues = values.map((value) => [
    quoteString(value.path.join('.')),
    ...(hasComments ? [value.comments] : []),
    value.value,
    ...(hasUnit ? [value.unit ?? ''] : []),
  ]);

  return (
    <table className="table-values">
      <thead>
        <tr>
          {headers.map((header) => (
            <th>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cellValues.map((row) => (
          <tr>
            {row.map((cell) => (
              <td>{cell?.toString()}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type ConstantsPageProps = {
  version: string;
};

export function ConstantsPage({ version }: ConstantsPageProps) {
  const { loading, error, constants } = useConstants(version);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="calc(100vh - 120px - 32px)">
        <Loader size="lg" />
        <Text ml="md">Loading constants...</Text>
      </Flex>
    );
  }

  if (error || !constants) {
    return (
      <Flex direction="column" align="center" justify="center" h="calc(100vh - 120px - 32px)">
        <Text color="red" size="lg" mb="md">
          {error || 'Failed to load constants'}
        </Text>
        <Text size="sm" color="dimmed">
          Please try refreshing the page or contact support if the issue persists.
        </Text>
      </Flex>
    );
  }

  const categories = constants as DocCategory[];

  return (
    <Grid>
      <Grid.Col span={10}>
        <ScrollArea scrollbars="y" style={{ flex: 1 }} h="calc(100vh - 120px - 32px)">
          <Title order={1}>Constants</Title>
          <br />
          This list lets you explore the constants that are used in the Emissions Calculators. The constants and their
          values are one of the primary elements that are used to calculate the emissions in each calculator.
          <div>
            {categories.map((category) => (
              <div key={category.name}>
                <Divider mt="lg" />
                <Title id={category.name} order={2} my="lg">
                  {category.name}
                </Title>
                {category.sections.map((section) => (
                  <div key={section.name}>
                    <Title order={3} mt="md">
                      {section.name}
                    </Title>
                    <table className="table-comments">
                      <tbody>
                        {section.comments
                          .map((c) => ({ text: c.text, typeDisplay: renderCommentType(c.type), type: c.type }))
                          .sort((a, b) => a.typeDisplay.localeCompare(b.typeDisplay))
                          .map(renderComment)}
                      </tbody>
                    </table>
                    <div className="gap" />
                    <SectionValuesTable values={section.values} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </ScrollArea>
      </Grid.Col>
      <Grid.Col span={2}>
        <Flex direction="column" style={{ overflow: 'hidden' }}>
          <Title order={4} p="sm">
            Categories
          </Title>
          {categories.map((category) => (
            <NavLink
              key={category.name}
              href={`#${category.name}`}
              label={category.name}
              onClick={() => {
                const element = document.getElementById(category.name);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            />
          ))}
        </Flex>
      </Grid.Col>
    </Grid>
  );
}
